import React, { useEffect, useMemo, useRef, useState } from 'react';
import { announcementService } from '../services/announcementService';
import { useAuth } from '../context/AuthContext';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Loading from '../components/common/Loading';
import StatusText from '../components/common/StatusText';

const AnnouncementsPage = () => {
  const { user, hasRole } = useAuth();
  const canCreate = hasRole('Manager') || hasRole('Admin');
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    title: '',
    content: '',
    isPinned: false,
    expiresAt: ''
  });
  const [expandedId, setExpandedId] = useState(null);
  const contentRef = useRef(null);

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await announcementService.getAnnouncements();
      setAnnouncements(response.data || []);
    } catch (err) {
      console.error('Failed to load announcements:', err);
      setError('Failed to load announcements.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      setSaving(true);
      await announcementService.createAnnouncement({
        title: form.title,
        content: form.content,
        isPinned: form.isPinned,
        expiresAt: form.expiresAt || null
      });
      setForm({ title: '', content: '', isPinned: false, expiresAt: '' });
      loadAnnouncements();
    } catch (err) {
      console.error('Failed to create announcement:', err);
      setError(err.response?.data?.message || 'Failed to create announcement.');
    } finally {
      setSaving(false);
    }
  };

  const applyContentChange = (nextText, selectionStart, selectionEnd) => {
    setForm(prev => ({ ...prev, content: nextText }));
    requestAnimationFrame(() => {
      if (contentRef.current) {
        contentRef.current.focus();
        contentRef.current.setSelectionRange(selectionStart, selectionEnd);
      }
    });
  };

  const handleBold = () => {
    const textarea = contentRef.current;
    if (!textarea) return;
    const { selectionStart, selectionEnd } = textarea;
    const value = form.content;

    if (selectionStart === selectionEnd) {
      const nextValue = `${value.slice(0, selectionStart)}****${value.slice(selectionEnd)}`;
      applyContentChange(nextValue, selectionStart + 2, selectionStart + 2);
      return;
    }

    const selectedText = value.slice(selectionStart, selectionEnd);
    const wrapped = `**${selectedText}**`;
    const nextValue = `${value.slice(0, selectionStart)}${wrapped}${value.slice(selectionEnd)}`;
    applyContentChange(nextValue, selectionStart + 2, selectionEnd + 2);
  };

  const handleBullets = () => {
    const textarea = contentRef.current;
    if (!textarea) return;
    const { selectionStart, selectionEnd } = textarea;
    const value = form.content;

    if (selectionStart === selectionEnd) {
      const nextValue = `${value.slice(0, selectionStart)}- ${value.slice(selectionEnd)}`;
      applyContentChange(nextValue, selectionStart + 2, selectionStart + 2);
      return;
    }

    const selectedText = value.slice(selectionStart, selectionEnd);
    const updatedSelection = selectedText
      .split('\n')
      .map(line => {
        if (line.trim() === '') return line;
        if (/^\s*[-*]\s+/.test(line)) return line;
        return `- ${line}`;
      })
      .join('\n');

    const nextValue = `${value.slice(0, selectionStart)}${updatedSelection}${value.slice(selectionEnd)}`;
    applyContentChange(nextValue, selectionStart, selectionStart + updatedSelection.length);
  };

  const handleAcknowledge = async (announcementId) => {
    try {
      await announcementService.acknowledgeAnnouncement(announcementId);
      loadAnnouncements();
    } catch (err) {
      console.error('Failed to acknowledge announcement:', err);
      setError(err.response?.data?.message || 'Failed to acknowledge announcement.');
    }
  };

  const getAcknowledgementStatus = (announcement) => {
    const acks = announcement.acknowledgements || [];
    const acknowledgedBy = acks.map(a => a.user).filter(Boolean);
    const isAcknowledged = acknowledgedBy.some(u => String(u.id) === String(user?.id));
    return { isAcknowledged, acknowledgedBy };
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString();
  };

  const contentPreview = (text) => {
    if (!text) return '';
    const flat = text.replace(/\s+/g, ' ').trim();
    if (flat.length <= 160) return flat;
    return `${flat.slice(0, 160)}...`;
  };

  const creatorName = (creator) => {
    if (!creator) return 'Unknown';
    return `${creator.firstName} ${creator.lastName}`.trim();
  };

  const sortedAnnouncements = useMemo(() => announcements, [announcements]);

  if (loading) return <Loading />;

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Announcements</h1>
        <p className="text-gray-600">Company-wide updates from managers and admins</p>
      </div>

      {canCreate && (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <h2 className="text-lg font-semibold mb-4">Create Announcement</h2>
          <form onSubmit={handleSubmit}>
            <Input
              label="Title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
            />
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
              <div className="flex gap-2 mb-2">
                <button
                  type="button"
                  onClick={handleBullets}
                  className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
                >
                  Bullet list
                </button>
                <button
                  type="button"
                  onClick={handleBold}
                  className="px-3 py-1 text-sm border rounded font-semibold hover:bg-gray-50"
                >
                  Bold
                </button>
                <span className="text-xs text-gray-500 self-center">
                  Use **bold** or - for bullets
                </span>
              </div>
              <textarea
                ref={contentRef}
                name="content"
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                rows="4"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Share an update with the team..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Expires At (Optional)"
                type="date"
                value={form.expiresAt}
                onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
              />
              <div className="flex items-center gap-3 mt-6">
                <input
                  type="checkbox"
                  checked={form.isPinned}
                  onChange={(e) => setForm({ ...form, isPinned: e.target.checked })}
                  id="announcement-pin"
                />
                <label htmlFor="announcement-pin" className="text-sm text-gray-700">
                  Pin to top
                </label>
              </div>
            </div>

            {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
            <div className="flex justify-end">
              <Button type="submit" disabled={saving}>
                {saving ? 'Posting...' : 'Post Announcement'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {announcements.length === 0 ? (
        <div className="bg-white p-6 rounded-lg shadow-md text-gray-600">
          No announcements yet.
        </div>
      ) : (
        <div className="space-y-4">
          {sortedAnnouncements.map((announcement) => {
            const { isAcknowledged, acknowledgedBy } = getAcknowledgementStatus(announcement);
            const isExpanded = expandedId === announcement.id;
            return (
              <div key={announcement.id} className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-gray-900">{announcement.title}</h3>
                      {announcement.isPinned && (
                        <span className="text-xs uppercase tracking-wide bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                          Pinned
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      By {creatorName(announcement.creator)}
                    </p>
                  </div>
                  <div className="text-right text-xs text-gray-400">
                    <div>Posted: {formatDate(announcement.createdAt)}</div>
                    <div>Expires: {formatDate(announcement.expiresAt)}</div>
                  </div>
                </div>

                <div className="mt-3 text-gray-700">
                  {isExpanded ? (
                    <StatusText text={announcement.content} />
                  ) : (
                    <p className="whitespace-pre-wrap">{contentPreview(announcement.content)}</p>
                  )}
                </div>

                <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => setExpandedId(isExpanded ? null : announcement.id)}
                    className="text-sm text-primary hover:underline"
                  >
                    {isExpanded ? 'Show less' : 'Read more'}
                  </button>

                  <div className="flex items-center gap-3">
                    <span className={`text-xs px-2 py-1 rounded ${
                      isAcknowledged ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {isAcknowledged ? 'Acknowledged' : 'Unread'}
                    </span>
                    {!isAcknowledged && (
                      <Button size="sm" variant="primary" onClick={() => handleAcknowledge(announcement.id)}>
                        Acknowledge
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-4 text-sm text-gray-600">
                  Acknowledged by {acknowledgedBy.length} user{acknowledgedBy.length === 1 ? '' : 's'}
                </div>

                {canCreate && acknowledgedBy.length > 0 && (
                  <div className="mt-2 text-xs text-gray-500">
                    {acknowledgedBy.map(u => `${u.firstName} ${u.lastName}`).join(', ')}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AnnouncementsPage;
